import type { IconName, StatusKind } from "@sagot/ui";
import type { ChannelOut, ChannelStatus, ChannelType } from "../shared/types";

/** Card order on the page (mockup order); unknown types are appended. */
export const CHANNEL_ORDER: readonly ChannelType[] = ["widget", "webapp", "mobile", "messenger", "email", "api", "slack", "teams"];

export const CHANNEL_ICON: Readonly<Record<ChannelType, IconName>> = {
  widget: "globe",
  webapp: "app-window",
  mobile: "smartphone",
  messenger: "message-circle",
  email: "mail",
  api: "code",
  playground: "flask",
  slack: "users",
  teams: "users",
};

const STATUS_KIND: Readonly<Record<ChannelStatus, StatusKind>> = {
  active: "success",
  pending: "neutral",
  disconnected: "neutral",
  error: "danger",
  disabled: "neutral",
};

export function statusKind(status: ChannelStatus): StatusKind {
  return STATUS_KIND[status];
}

export function sortChannels(channels: readonly ChannelOut[]): ChannelOut[] {
  const rank = (type: ChannelType) => {
    const index = CHANNEL_ORDER.indexOf(type);
    return index === -1 ? CHANNEL_ORDER.length : index;
  };
  return [...channels].filter((c) => c.type !== "playground").sort((a, b) => rank(a.type) - rank(b.type));
}

function text(detail: ChannelOut["detail"], key: string): string | null {
  const value = detail[key];
  return typeof value === "string" && value.length > 0 ? value : null;
}

function count(detail: ChannelOut["detail"], key: string): number {
  const value = detail[key];
  if (typeof value === "number") return value;
  return Array.isArray(value) ? value.length : 0;
}

export interface MessengerDetail {
  connected: boolean;
  pageName: string | null;
}

export function messengerDetail(channel: ChannelOut | undefined): MessengerDetail {
  const pageName = channel ? text(channel.detail, "page_name") : null;
  return { connected: Boolean(channel && text(channel.detail, "page_id")), pageName };
}

export type ReplyMode = "draft" | "auto";

export interface EmailDetail {
  configured: boolean;
  inboundAddress: string | null;
  forwardingAddress: string | null;
  replyMode: ReplyMode;
}

export function emailDetail(channel: ChannelOut | undefined): EmailDetail {
  const detail = channel?.detail ?? {};
  const inboundAddress = text(detail, "inbound_address");
  return {
    configured: inboundAddress !== null,
    inboundAddress,
    forwardingAddress: text(detail, "forwarding_address"),
    replyMode: text(detail, "reply_mode") === "auto" ? "auto" : "draft",
  };
}

export const widgetDomainCount = (channel: ChannelOut) => count(channel.detail, "allowed_domains");
export const apiKeyCount = (channel: ChannelOut) => count(channel.detail, "active_keys");

export interface ChannelKpis {
  active: number;
  available: number;
  conversations: number;
  top: { type: ChannelType; pct: number } | null;
}

/** Mobile is no channel to switch on (the assistant is embedded in the customer's own app), so it never counts as available or active. */
const isConnectable = (channel: ChannelOut) => channel.type !== "mobile";

export function channelKpis(allChannels: readonly ChannelOut[]): ChannelKpis {
  const channels = allChannels.filter(isConnectable);
  const conversations = allChannels.reduce((sum, c) => sum + c.conversations_7d, 0);
  const leader = allChannels.reduce<ChannelOut | null>((best, c) => (c.conversations_7d > (best?.conversations_7d ?? 0) ? c : best), null);
  return {
    active: channels.filter((c) => c.enabled).length,
    available: channels.length,
    conversations,
    top: leader && conversations > 0 ? { type: leader.type, pct: Math.round((leader.conversations_7d / conversations) * 100) } : null,
  };
}

/** Channels shown in the performance table: switched on, or with conversations in the window (Mobile only for past conversations). */
export function performanceChannels(channels: readonly ChannelOut[]): ChannelOut[] {
  return sortChannels(channels).filter((c) => (isConnectable(c) && c.enabled) || c.conversations_7d > 0);
}

const MS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;

/** `1.3s` under a minute, `2m 10s` above (mockup format). */
export function formatResponse(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return "—";
  const seconds = ms / MS_PER_SECOND;
  if (seconds < SECONDS_PER_MINUTE) return `${seconds.toFixed(1)}s`;
  const minutes = Math.floor(seconds / SECONDS_PER_MINUTE);
  return `${String(minutes)}m ${String(Math.round(seconds - minutes * SECONDS_PER_MINUTE))}s`;
}

export function percent(rate: number | null | undefined): number | null {
  return rate === null || rate === undefined ? null : Math.round(rate * 100);
}
